import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Zap, Mail, MapPin, Loader2, CheckCircle2, AlertCircle, RefreshCw, ShieldCheck, ArrowRight, Clock, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { quickPostStatus, quickPostResend, quickPostCheckEmail } from '../services/api';
import { useAuth } from '../context/AuthContext';

function fmtEur(n) {
  if (n == null) return '—';
  try { return `€${Number(n).toFixed(2)}`; } catch { return `€${n}`; }
}

function PendingVerification({ delivery, guestToken, onResend, resending }) {
  return (
    <div className="bg-white rounded-2xl shadow-xl shadow-amber-100/40 border border-amber-100 p-6 sm:p-8" data-testid="quick-post-pending-verification">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-white shadow-lg shadow-emerald-100 ring-1 ring-emerald-100 mb-5 p-2.5">
          <img src="/jd-icon.png" alt="JustDeliver" className="w-full h-full object-contain" />
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold mb-3">
          <Mail className="w-3 h-3" /> CHECK YOUR EMAIL
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Inter', sans-serif" }}>
          One last step
        </h2>
        <p className="mt-3 text-gray-700">
          We sent a verification link to <strong>{delivery.guest_email || 'your email'}</strong>.
        </p>
        <p className="mt-2 text-sm text-gray-500">
          Please verify your email address to publish your delivery request and receive bids from drivers.
        </p>
      </div>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-900 font-semibold mb-1">Didn&apos;t get the email?</p>
        <p className="text-xs text-amber-800 mb-3">Check your spam folder, or re-send the link below. Links expire in 24 hours.</p>
        <Button
          variant="outline"
          onClick={onResend}
          disabled={resending}
          className="border-amber-400 text-amber-900 hover:bg-amber-100"
          data-testid="quick-post-resend-btn"
        >
          {resending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Re-send verification email</>}
        </Button>
      </div>

      <div className="mt-6 text-center">
        <Link to="/quick-post" className="text-sm text-gray-500 hover:text-gray-700 underline">
          Post another delivery instead
        </Link>
      </div>
    </div>
  );
}

function OfferCard({ offer, onAccept, accepting }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 shadow-sm hover:shadow-md transition" data-testid={`quick-post-offer-${offer.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-gray-900 truncate">{offer.driver_name || 'JustDeliver Driver'}</p>
          {offer.message && (
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">{offer.message}</p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-black text-green-700">{fmtEur(offer.price)}</p>
          <p className="text-[11px] text-gray-500">driver bid</p>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          onClick={() => onAccept(offer)}
          disabled={accepting}
          className="bg-green-700 hover:bg-green-800 text-white font-semibold"
          data-testid={`quick-post-accept-offer-${offer.id}`}
        >
          {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Accept &amp; pay <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function VerifiedView({ delivery, guestToken }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accepting, setAccepting] = useState(false);

  const handleAccept = useCallback(async (offer) => {
    setAccepting(true);
    try {
      // Decide between login vs signup based on guest email existence
      const guestEmail = delivery.guest_email;
      if (user && user.role === 'customer' && user.email?.toLowerCase() === (guestEmail || '').toLowerCase()) {
        // Already logged in with matching email — go straight to claim + accept flow
        navigate(`/customer?claim=${encodeURIComponent(guestToken)}&offer=${encodeURIComponent(offer.id)}`);
        return;
      }
      const { data } = await quickPostCheckEmail(guestEmail);
      const next = encodeURIComponent(`/customer?claim=${guestToken}&offer=${offer.id}`);
      if (data.exists) {
        navigate(`/login?next=${next}&email=${encodeURIComponent(guestEmail)}`);
      } else {
        navigate(`/register?next=${next}&email=${encodeURIComponent(guestEmail)}&phone=${encodeURIComponent(delivery.guest_phone || '')}`);
      }
    } catch (e) {
      toast.error('Could not start the accept flow. Please try again.');
    } finally {
      setAccepting(false);
    }
  }, [delivery, guestToken, navigate, user]);

  const offers = delivery.offers || [];

  return (
    <div className="space-y-6">
      {/* Status header */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-testid="quick-post-live-banner">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-green-900">Your delivery request is now live!</p>
            <p className="text-sm text-green-800 mt-0.5">Drivers can begin submitting bids. You&apos;ll see them appear below in real-time.</p>
          </div>
        </div>
      </div>

      {/* Delivery summary */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-[11px] font-bold">
            <Zap className="w-3 h-3" fill="currentColor" /> QUICK POST
          </div>
          <p className="text-xs text-gray-500">{delivery.delivery_ref}</p>
        </div>
        <h2 className="text-xl font-black text-gray-900 mb-1">{delivery.title}</h2>
        <p className="text-sm text-gray-600 mb-4 whitespace-pre-wrap">{delivery.item_description}</p>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] uppercase text-gray-500 font-semibold tracking-wide">Pickup</p>
              <p className="text-gray-900">{delivery.pickup_location}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[11px] uppercase text-gray-500 font-semibold tracking-wide">Delivery</p>
              <p className="text-gray-900">{delivery.dropoff_location}</p>
            </div>
          </div>
        </div>

        {Array.isArray(delivery.photos) && delivery.photos.length > 0 && (
          <div className="mt-4 flex gap-2 flex-wrap">
            {delivery.photos.map((src, i) => (
              <img key={i} src={src} alt={`Item ${i + 1}`} className="w-20 h-20 object-cover rounded-md border border-gray-200" />
            ))}
          </div>
        )}
      </div>

      {/* Offers */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-900">
            Driver bids {offers.length > 0 && <span className="text-gray-500 font-medium">({offers.length})</span>}
          </h3>
        </div>
        {offers.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center" data-testid="quick-post-no-offers">
            <Clock className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="font-semibold text-gray-900">Waiting for drivers to bid…</p>
            <p className="text-sm text-gray-500 mt-1">This usually takes just a few minutes. We&apos;ll keep this page up to date.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {offers.map((o) => (
              <OfferCard key={o.id} offer={o} onAccept={handleAccept} accepting={accepting} />
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 flex items-start gap-1.5 bg-gray-50 rounded-md p-3">
        <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
        Driver contact details unlock once you accept a bid and complete payment. We&apos;ll prompt you to create an account or sign in at that point — your email and phone will be pre-filled.
      </p>
    </div>
  );
}

export default function QuickPostStatusPage() {
  const { guestToken } = useParams();
  const [searchParams] = useSearchParams();
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);
  const toastShown = useRef(false);

  const load = useCallback(async () => {
    if (!guestToken) {
      setError('Missing token.');
      setLoading(false);
      return;
    }
    try {
      const { data } = await quickPostStatus(guestToken);
      setDelivery(data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Delivery not found.');
    } finally {
      setLoading(false);
    }
  }, [guestToken]);

  useEffect(() => {
    load();
  }, [load]);

  // One-time toast based on URL status param. Use setTimeout to defer the
  // notification off React's commit phase so it doesn't fight the strict
  // set-state-in-effect lint rule.
  useEffect(() => {
    const s = searchParams.get('status');
    if (!s || toastShown.current) return;
    const t = setTimeout(() => {
      toastShown.current = true;
      if (s === 'verified') toast.success('Email verified — your delivery is live!');
      else if (s === 'already_verified') toast.info('This delivery is already verified.');
      else if (s === 'expired') toast.error('Verification link expired. Please re-send.');
      else if (s === 'invalid') toast.error('Invalid verification link.');
      else if (s === 'pending_verification') toast.message('Almost there — verify your email to publish.');
    }, 50);
    return () => clearTimeout(t);
  }, [searchParams]);

  // Poll for new offers every 15s when verified
  useEffect(() => {
    if (!delivery || !delivery.guest_email_verified) return;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [delivery, load]);

  const handleResend = async () => {
    setResending(true);
    try {
      await quickPostResend(guestToken);
      toast.success('Verification email re-sent. Please check your inbox.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not re-send. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-white">
      <header className="bg-white border-b border-gray-200">
        <div className="relative max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" aria-label="JustDeliver home" data-testid="quick-post-status-logo">
            <img src="/logo.png" alt="JustDeliver" className="h-8 sm:h-9 w-auto" />
          </Link>
          <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-900 text-xs font-bold">
            <Zap className="w-3.5 h-3.5" fill="currentColor" /> QUICK POST
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12" data-testid="quick-post-status-page">
        {loading ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
            <p className="mt-3 text-sm text-gray-500">Loading your delivery…</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center" data-testid="quick-post-status-error">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p className="font-bold text-gray-900">{error}</p>
            <Link to="/quick-post">
              <Button className="mt-4" variant="outline">Post a new delivery</Button>
            </Link>
          </div>
        ) : delivery?.guest_email_verified ? (
          <VerifiedView delivery={delivery} guestToken={guestToken} />
        ) : (
          <PendingVerification
            delivery={delivery}
            guestToken={guestToken}
            onResend={handleResend}
            resending={resending}
          />
        )}
      </main>
    </div>
  );
}
